import {
  Add,
  AllInclusive,
  Close,
  Delete,
  Email,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface RefreshSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  listId: string;
  currentRefreshInterval: string | null;
  currentMaxRecordsPerRefresh: number | null;
  currentEmailNotificationEnabled?: boolean;
  currentEmailNotificationRecipients?: string[];
  currentEmailNotificationFormat?: string;
  onSave: (audience: any) => void;
}

const RefreshSettingsDialog: React.FC<RefreshSettingsDialogProps> = ({
  open,
  onClose,
  getToken,
  companyId,
  listId,
  currentRefreshInterval,
  currentMaxRecordsPerRefresh,
  currentEmailNotificationEnabled,
  currentEmailNotificationRecipients,
  currentEmailNotificationFormat,
  onSave,
}) => {
  const [refreshInterval, setRefreshInterval] = useState<string>("none");
  const [maxRecordsPerRefresh, setMaxRecordsPerRefresh] = useState<number | null>(null);
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailFormat, setEmailFormat] = useState<string>("csv");
  const [newRecipient, setNewRecipient] = useState("");
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize values when dialog opens
  useEffect(() => {
    if (open) {
      setRefreshInterval(currentRefreshInterval || "none");
      setMaxRecordsPerRefresh(currentMaxRecordsPerRefresh);
      setEmailNotificationEnabled(currentEmailNotificationEnabled || false);
      setEmailRecipients(currentEmailNotificationRecipients || []);
      setEmailFormat(currentEmailNotificationFormat || "csv");
      setNewRecipient("");
      setRecipientError(null);
      setError(null);
    }
  }, [
    open,
    currentRefreshInterval,
    currentMaxRecordsPerRefresh,
    currentEmailNotificationEnabled,
    currentEmailNotificationRecipients,
    currentEmailNotificationFormat,
  ]);

  const validateEmail = (email: string): boolean => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleAddRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email) return;

    if (!validateEmail(email)) {
      setRecipientError("Please enter a valid email address");
      return;
    }

    if (emailRecipients.includes(email)) {
      setRecipientError("This email is already in the list");
      return;
    }

    setEmailRecipients([...emailRecipients, email]);
    setNewRecipient("");
    setRecipientError(null);
  };

  const handleRemoveRecipient = (email: string) => {
    setEmailRecipients(emailRecipients.filter((r) => r !== email));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddRecipient();
    }
  };

  const handleSave = async () => {
    // Validate email settings
    if (emailNotificationEnabled && emailRecipients.length === 0) {
      setError("Please add at least one email recipient or disable email notifications.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.put(
        `${hostname}/lists/${listId}/audience/audience/refresh-settings`,
        {
          companyId,
          refreshInterval,
          maxRecordsPerRefresh,
          emailNotificationEnabled,
          emailNotificationRecipients: emailRecipients,
          emailNotificationFormat: emailFormat,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      onSave(response.data.audience);
      onClose();
    } catch (err) {
      setError(parseError(err) || "Failed to save refresh settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Refresh />
        Refresh Settings
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Configure how often the audience refreshes. New contacts matching your
          filters will be automatically imported.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Refresh Interval</InputLabel>
          <Select
            value={refreshInterval}
            label="Refresh Interval"
            onChange={(e) => setRefreshInterval(e.target.value)}
          >
            <MenuItem value="none">None (Manual only)</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="3days">Every 3 Days</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          Max new records per refresh
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <TextField
            size="small"
            type="number"
            value={maxRecordsPerRefresh ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              setMaxRecordsPerRefresh(val === "" ? null : parseInt(val, 10));
            }}
            placeholder="Unlimited"
            sx={{ flex: 1 }}
            InputProps={{
              inputProps: { min: 1 },
            }}
          />
          <Tooltip title="Set to unlimited">
            <IconButton
              size="small"
              onClick={() => setMaxRecordsPerRefresh(null)}
              color={maxRecordsPerRefresh === null ? "primary" : "default"}
            >
              <AllInclusive />
            </IconButton>
          </Tooltip>
        </Box>
        {maxRecordsPerRefresh === null && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}
          >
            <AllInclusive sx={{ fontSize: 14 }} /> Unlimited records per refresh
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Email Notification Settings */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Email color="action" />
          <Typography variant="subtitle2">
            Email Notifications
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={emailNotificationEnabled}
              onChange={(e) => setEmailNotificationEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="Email new records when audience refreshes"
          sx={{ mb: 2 }}
        />

        <Collapse in={emailNotificationEnabled}>
          <Box sx={{ pl: 0, mt: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Recipients
            </Typography>

            {/* Email input */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Enter email address"
                value={newRecipient}
                onChange={(e) => {
                  setNewRecipient(e.target.value);
                  setRecipientError(null);
                }}
                onKeyPress={handleKeyPress}
                error={!!recipientError}
                helperText={recipientError || "Press Enter to add"}
                sx={{ flex: 1 }}
              />
              <Tooltip title="Add recipient">
                <IconButton
                  size="small"
                  onClick={handleAddRecipient}
                  color="primary"
                  sx={{ mt: 0.5 }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Recipients list */}
            {emailRecipients.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {emailRecipients.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={() => handleRemoveRecipient(email)}
                    deleteIcon={<Delete fontSize="small" />}
                  />
                ))}
              </Box>
            )}

            {/* Export format selection */}
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Attachment Format</InputLabel>
              <Select
                value={emailFormat}
                label="Attachment Format"
                onChange={(e) => setEmailFormat(e.target.value)}
              >
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="xlsx">Excel (XLSX)</MenuItem>
              </Select>
            </FormControl>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              New records will be sent as an attachment in the selected format.
            </Typography>
          </Box>
        </Collapse>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton onClick={onClose} variant="text">
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={loading}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RefreshSettingsDialog;
